import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, delay, expand, first, map, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ProgressStatusEnum } from '../enums/progress-status.enum';
import { OrgUnitDto } from '../models/org-unit-dto';
import { OrgUnitWrite } from '../models/org-unit-write';
import { ProcessTrackerDto } from '../models/process-tracker-dto';
import { StockQuantityChangeReasonDto } from '../models/stock-quantity-change-reason-dto';
import { TreeNode } from '../tree.utils';

const IMPORT_POLL_TIMEOUT_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class OrgUnitsService {
  constructor(private httpClient: HttpClient) {}

  getOrgUnitsByOrgId(orgId: number): Observable<TreeNode<OrgUnitDto>> {
    return this.httpClient
      .get<TreeNode<OrgUnitDto>>(`${environment.backendUrl}/api/v1/org/${orgId}/orgunits`)
      .pipe(map((response) => response));
  }

  getAdminOrgUnitsByOrgId(organisationId: number): Observable<TreeNode<OrgUnitDto>> {
    return this.httpClient
      .get<TreeNode<OrgUnitDto>>(`${environment.backendUrl}/api/admin/v1/orgunits/organisation/${organisationId}`)
      .pipe(map((response) => response));
  }

  createAdminOrgUnit(data: OrgUnitWrite): Observable<OrgUnitDto> {
    return this.httpClient.post<OrgUnitDto>(`${environment.backendUrl}/api/admin/v1/orgunits`, data);
  }

  updateAdminOrgUnit(id: number, data: OrgUnitWrite): Observable<OrgUnitDto> {
    return this.httpClient.put<OrgUnitDto>(`${environment.backendUrl}/api/admin/v1/orgunits/${id}`, data);
  }

  markAdminOrgUnitAsDefault(id: number): Observable<boolean> {
    return this.httpClient.put<boolean>(`${environment.backendUrl}/api/admin/v1/orgunits/${id}/markAsDefault`, {});
  }

  importAdminOrgUnitsCsv(
    organisationId: number,
    file: File,
    useCommaDelimiter: boolean,
  ): Observable<ProcessTrackerDto> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const params = new HttpParams().set('useCommaDelimiter', String(useCommaDelimiter));
    return this.httpClient.post<ProcessTrackerDto>(
      `${environment.backendUrl}/api/admin/v1/orgunits/organisation/${organisationId}/csv-import`,
      formData,
      { params },
    );
  }

  getLastAdminOrgUnitsCsvImport(organisationId: number): Observable<ProcessTrackerDto | undefined> {
    return this.httpClient
      .get<ProcessTrackerDto>(
        `${environment.backendUrl}/api/admin/v1/orgunits/organisation/${organisationId}/csv-import/status`,
      )
      .pipe(catchError((err) => (err.status === 404 ? of(undefined) : throwError(() => err))));
  }

  waitAdminOrgUnitsCsvImport(organisationId: number): Observable<ProcessTrackerDto> {
    return this.getLastAdminOrgUnitsCsvImport(organisationId).pipe(
      expand((process) =>
        !process || process.status === ProgressStatusEnum.Ongoing
          ? this.getLastAdminOrgUnitsCsvImport(organisationId).pipe(delay(1500))
          : EMPTY,
      ),
      first((process): process is ProcessTrackerDto => !!process && process.status !== ProgressStatusEnum.Ongoing),
      timeout({ first: IMPORT_POLL_TIMEOUT_MS }),
    );
  }

  getOperatorOrgUnitsByOrgId(orgId: number): Observable<TreeNode<OrgUnitDto>> {
    return this.httpClient
      .get<TreeNode<OrgUnitDto>>(`${environment.backendUrl}/api/v1/org/${orgId}/orgunits`)
      .pipe(map((response) => response));
  }

  getStockReductionReasons(): Observable<StockQuantityChangeReasonDto[]> {
    return this.httpClient.get<StockQuantityChangeReasonDto[]>(
      `${environment.backendUrl}/api/operator/v1/stock-reduction-reasons`,
    );
  }
}
